import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchRequisitesData } from '../services/dataApi';
import { fetchPageData } from '../services/corezoidApi';
import PageLoader from '../components/PageLoader';
import RequisitesTable from '../components/RequisitesTable';
import AddRequisiteModal from '../components/AddRequisiteModal';
import SearchBar from '../components/SearchBar';
import { searchInData } from '../utils/searchUtils';
import useUrlPagination from '../hooks/useUrlPagination';
import useDebouncedValue from '../hooks/useDebouncedValue';
import './Page.css';

const Requisites = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRequisite, setEditingRequisite] = useState(null);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchRequisitesData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    // Extract data array
    let requisites = [];
    if (data && data.data && Array.isArray(data.data)) {
      requisites = data.data;
    } else if (Array.isArray(data)) {
      requisites = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        requisites = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        requisites = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(requisites, searchTerm);
    
    // Preserve data structure
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const requisitesCount = pagination?.total ?? (filteredData?.data?.length || 0);

  const buildChangedFields = (originalData, inputData, fieldMapping) => {
    const changed = {};

    Object.keys(fieldMapping).forEach(formField => {
      const possibleKeys = fieldMapping[formField];
      const newValue = inputData[formField];

      if (newValue === undefined || newValue === '') {
        return;
      }

      let keyFound = false;
      for (const key of possibleKeys) {
        if (key in originalData) {
          keyFound = true;
          if (originalData[key] !== newValue) {
            changed[key] = newValue;
          }
          break;
        }
      }

      if (!keyFound) {
        changed[formField] = newValue;
      }
    });

    return changed;
  };

  const handleEditRequisite = async (requisiteData, originalRequisite) => {
    try {
      // Get requisite ref for update
      const requisiteRef = originalRequisite?.ref;
      
      if (!requisiteRef) {
        console.error('Requisite ref not found:', originalRequisite);
        return { success: false, error: 'Requisite ref not found' };
      }

      // Get all original requisite data (now at top level)
      const originalData = originalRequisite;
      
      // Map form fields to possible original key variants
      const fieldMapping = {
        AccountNumber: ['AccountNumber', 'accountNumber', 'account_number'],
        Bank: ['Bank', 'bank'],
        Currency: ['Currency', 'currency'],
        NameSurname: ['NameSurname', 'nameSurname', 'name_surname'],
        status: ['status', 'Status', 'STATUS']
      };
      
      const changedData = buildChangedFields(originalData, requisiteData, fieldMapping);
      if (Object.keys(changedData).length === 0) {
        return { success: true, skipped: true };
      }

      // Form request for updating requisite via API 2.0
      const ops = [{
        type: "modify",
        obj: "task",
        data: changedData,
        ref: requisiteRef
      }];

      const result = await fetchPageData('requisites', ops, 30, false);

      if (result.success && result.data) {
        // Check response format
        const responseData = result.data;
        
        // Check request_proc
        if (responseData.request_proc === 'ok' && responseData.ops && responseData.ops.length > 0) {
          const firstOp = responseData.ops[0];
          
          if (firstOp.proc === 'ok') {
            loadData();
            return { success: true };
          } else if (firstOp.proc === 'error') {
            const errorMsg = firstOp.description || 'Unknown error';
            return { success: false, error: errorMsg };
          }
        }
        
        // If response format is unexpected
        return { success: false, error: 'Unexpected response format' };
      } else {
        return { success: false, error: result.error || 'Failed to update requisite' };
      }
    } catch (err) {
      console.error('Error updating requisite:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  // Generate unique 9-character code with letters and numbers
  const generateUniqueRef = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const digits = '0123456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Ensure at least one digit is present
    if (!/\d/.test(result)) {
      const randomPosition = Math.floor(Math.random() * 9);
      result = result.substring(0, randomPosition) + 
               digits.charAt(Math.floor(Math.random() * digits.length)) + 
               result.substring(randomPosition + 1);
    }
    return result;
  };

  const handleAddRequisite = async (requisiteData) => {
    try {
      // Generate unique 9-character ref with letters and numbers
      const uniqueRef = generateUniqueRef();
      
      // Add lastUsed with current unix time and activeOrders with empty object
      const dataWithLastUsed = {
        ...requisiteData,
        lastUsed: Math.floor(Date.now() / 1000),
        activeOrders: {}
      };
      
      // Form request for creating requisite via API 2.0
      const ops = [{
        type: "create",
        obj: "task",
        data: dataWithLastUsed,
        ref: uniqueRef
      }];

      const result = await fetchPageData('requisites', ops, 30, false);

      if (result.success && result.data) {
        // Check response format
        const responseData = result.data;
        
        // Check request_proc
        if (responseData.request_proc === 'ok' && responseData.ops && responseData.ops.length > 0) {
          const firstOp = responseData.ops[0];
          
          if (firstOp.proc === 'ok') {
            loadData();
            return { success: true };
          } else if (firstOp.proc === 'error') {
            const errorMsg = firstOp.description || 'Unknown error';
            return { success: false, error: errorMsg };
          }
        }
        
        // If response format is unexpected
        return { success: false, error: 'Unexpected response format' };
      } else {
        return { success: false, error: result.error || 'Failed to create requisite' };
      }
    } catch (err) {
      console.error('Error creating requisite:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Requisites</h1>
          <p>Manage payout details, review account states, and keep active credentials easy to scan.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Directory</div>
            <div className="overview-value">{requisitesCount}</div>
            <div className="overview-meta">Visible records</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Search</div>
            <div className="overview-value">{searchTerm ? 'On' : 'Off'}</div>
            <div className="overview-meta">{searchTerm ? `Filter: ${searchTerm}` : 'Showing full list'}</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          <div className="page-data">
            {data ? (
              <>
                <div className="page-section-header">
                  <div className="page-section-title">
                    <h2>Requisite Directory</h2>
                    <p>Open a requisite to inspect details or update it.</p>
                  </div>
                  <div className="page-section-meta">
                    <div className="page-chip">50 requisites per page</div>
                    <div className="page-chip">{searchTerm ? 'Filtered view' : 'Live view'}</div>
                  </div>
                </div>
                <SearchBar 
                  onSearch={handleSearch}
                  value={searchTerm}
                  resultCount={pagination?.total || 0}
                  isUpdating={searchTerm !== debouncedSearchTerm}
                  placeholder="Search requisites..."
                />
                <RequisitesTable 
                  data={filteredData} 
                  onAdd={handleAddRequisite}
                  onEdit={handleEditRequisite}
                  onAddClick={() => setIsAddModalOpen(true)}
                pagination={pagination}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="page-placeholder">
                <p>No data received</p>
              </div>
            )}
          </div>
        </PageLoader>
        <AddRequisiteModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddRequisite}
        />
        <AddRequisiteModal
          isOpen={!!editingRequisite}
          onClose={() => setEditingRequisite(null)}
          onSubmit={handleEditRequisite}
          initialData={editingRequisite}
          isEdit={true}
        />
      </div>
    </div>
  );
};

export default Requisites;
